import {
  BODY_SLEEVE_LENGTH_NUMBERS,
  JACKET_TYPE,
  LINING_VALUES, SIZES_NUMBERS,
} from '@/common/constants';
import pricesApi from '@/api/pricesApi';
import flattenDeep from 'lodash/flattenDeep';

export default {
  namespaced: true,

  state: {
    prices: {},
    totalPrice: 0,
    jacketPrice: 0,
    oversizePrice: 0,
    sleeveLengthPrice: 0,
    bodyLengthPrice: 0,
    zipperHoodPrice: 0,
    liningPrice: 0,
    patchesPrice: 0,
    insertsPrice: 0,
    form: {
      currentValues: {
        sizeSelected: null,
        sleeveLengthSelected: null,
        bodyLengthSelected: null,
        snapStyleSelected: null,
        liningSelected: LINING_VALUES.QUILT_LINING,
      }
    },
    notes: '',
  },

  getters: {
    getPrices: (state) => state.prices,
    getCurrentValues: (state) => state.form.currentValues,
    getRevisionNotes: (state) => state.notes,
    getJacketPrice: (state) => state.jacketPrice,
    getTotalPrice: (state) => state.totalPrice,
    getOversizePrice: (state) => state.oversizePrice,
    getSleeveLengthPrice: (state) => state.sleeveLengthPrice,
    getBodyLengthPrice: (state) => state.bodyLengthPrice,
    getZipperHoodPrice: (state) => state.zipperHoodPrice,
    getLiningPrice: (state) => state.liningPrice,
    getPatchesPrice: (state) => state.patchesPrice,
    getInsertsPrice: (state) => state.insertsPrice,
  },

  mutations: {
    setOrderData (state, finalizeOrderData) {
      state.form = finalizeOrderData.form;
    },
    setTotalPrice (state, totalPrice) {
      state.totalPrice = totalPrice;
    },
    setJacketPrice (state, price) {
      state.jacketPrice = price;
    },
    setSleeveLengthPrice (state, price) {
      state.sleeveLengthPrice = price;
    },
    setBodyLengthPrice (state, price) {
      state.bodyLengthPrice = price;
    },
    setZipperHoodPrice (state, price) {
      state.zipperHoodPrice = price;
    },
    setLiningPrice (state, price) {
      state.liningPrice = price;
    },
    setPatchesPrice (state, price) {
      state.patchesPrice = price;
    },
    setInsertsPrice (state, price) {
      state.insertsPrice = price;
    },
    setPrices (state, prices) {
      state.prices = prices;
    },
    setSizeSelected (state, selected) {
      state.form.currentValues.sizeSelected = selected;
    },
    setSleeveLengthSelected (state, selected) {
      state.form.currentValues.sleeveLengthSelected = selected;
    },
    setBodyLengthSelected (state, selected) {
      state.form.currentValues.bodyLengthSelected = selected;
    },
    setSnapStyleSelected (state, selected) {
      state.form.currentValues.snapStyleSelected = selected;
    },
    setLiningSelected (state, selected) {
      state.form.currentValues.liningSelected = selected;
    },
    setOversizePrice (state, price) {
      state.oversizePrice = price;
    },
    setRevisionNotes (state, text) {
      state.notes = text;
    }
  },

  actions: {
    setOrderData ({ commit }, finalizeOrderData) {
      commit('setOrderData', finalizeOrderData);
    },
    getPrices ({ commit }) {
      return pricesApi.getPrices().then((response) => {
        commit('setPrices', response.data);
        return response.data;
      });
    },

    calculateJacketPrice ({ state, commit, rootState }) {
      if (rootState.design.jacket.type === JACKET_TYPE.SET_IN) {
        commit('setJacketPrice', state.prices.setInSP);
      }
      if (rootState.design.jacket.type === JACKET_TYPE.RAGLAN) {
        commit('setJacketPrice', state.prices.reglanSP);
      }
    },

    calculateOversizePrice ({ state, commit }) {
      if (state.form.currentValues.sizeSelected === SIZES_NUMBERS.XXL) {
        commit('setOversizePrice', state.prices.oversize2XLSP);
      } else if (state.form.currentValues.sizeSelected === SIZES_NUMBERS.XXXL) {
        commit('setOversizePrice', state.prices.oversize3XLSP);
      } else if (state.form.currentValues.sizeSelected === SIZES_NUMBERS.XXXXL) {
        commit('setOversizePrice', state.prices.oversize4XLSP);
      } else {
        commit('setOversizePrice', 0);
      }
    },

    calculateSleeveLengthPrice ({ state, commit }) {
      if (state.form.currentValues.sleeveLengthSelected !== BODY_SLEEVE_LENGTH_NUMBERS.NO_CHANGE) {
        commit('setSleeveLengthPrice', state.prices.extraSleeveOrBodyLengthSP);
      } else {
        commit('setSleeveLengthPrice', 0);
      }
    },

    calculateBodyLengthPrice ({ state, commit }) {
      if (state.form.currentValues.bodyLengthSelected !== BODY_SLEEVE_LENGTH_NUMBERS.NO_CHANGE) {
        commit('setBodyLengthPrice', state.prices.extraSleeveOrBodyLengthSP);
      } else {
        commit('setBodyLengthPrice', 0);
      }
    },

    calculateHoodedPrice ({ state, commit, rootState }) {
      if (rootState.design.jacket.collar.isHooded) {
        commit('setZipperHoodPrice', state.prices.zipperedHoodSP);
      } else {
        commit('setZipperHoodPrice', 0);
      }
    },

    calculateLiningPrice ({ state, commit }) {
      if (state.form.currentValues.liningSelected === LINING_VALUES.QUILT_LINING) {
        commit('setLiningPrice', state.prices.quiltedLiningSP);
      } else if (state.form.currentValues.liningSelected === LINING_VALUES.NYLON_LINING) {
        commit('setLiningPrice', state.prices.packetCordEdgeSP);
      }
    },

    calculatePatchesPrice ({ commit, rootState }) {
      let sum = 0;
      rootState.design.patches.ids.forEach((id) => {
        sum += rootState.design.patches.data[id].patchSize.sellingPrice;
      });
      commit('setPatchesPrice', sum);
    },

    calculateInsertsPrice ({ commit, rootState }) {
      let sum = 0;
      const filterPatchArray = rootState.design.patches.ids
        .filter((id) => !rootState.design.patches.data[id].isTextPatch);

      const arrayOfArray = filterPatchArray.map((id) => rootState.design.patches.data[id].inserts);

      flattenDeep(arrayOfArray).forEach((insert) => {
        sum += insert.sellingPrice;
      });

      commit('setInsertsPrice', sum);
    },

    calculateTotalPrice ({ dispatch, state, commit }) {
      dispatch('calculateJacketPrice');
      dispatch('calculateOversizePrice');
      dispatch('calculateSleeveLengthPrice');
      dispatch('calculateBodyLengthPrice');
      dispatch('calculateHoodedPrice');
      dispatch('calculateLiningPrice');
      dispatch('calculatePatchesPrice');
      dispatch('calculateInsertsPrice');

      let sum = 0;
      sum = state.jacketPrice
          + state.oversizePrice
          + state.sleeveLengthPrice
          + state.bodyLengthPrice
          + state.liningPrice
          + state.zipperHoodPrice + state.patchesPrice + state.insertsPrice;

      commit('setTotalPrice', sum);
    },
  }
};
